import { DeleteOutlined, DownloadOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { App as AntApp, Button, Empty, Input, Popconfirm, Select, Table, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApplications, useBulkDeleteProperties, useProperties } from "../api/queries.js";
import type { PropertyEntry } from "../api/types.js";
import { CreatePropertyModal } from "../components/CreatePropertyModal.js";
import { ImportModal } from "../components/ImportModal.js";
import { PropertyDrawer, type OpenProperty } from "../components/PropertyDrawer.js";

const PAGE_SIZE = 25;

export function PropertiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const application = searchParams.get("app") ?? "";
  const [nameFilter, setNameFilter] = useState("");
  const [valueFilter, setValueFilter] = useState("");
  // What the search boxes display; applied to the query on Enter/search.
  const [nameDraft, setNameDraft] = useState("");
  const [valueDraft, setValueDraft] = useState("");
  const [targetFilter, setTargetFilter] = useState("");
  const [targetDraft, setTargetDraft] = useState("");
  const [page, setPage] = useState(1);
  const [openProperty, setOpenProperty] = useState<OpenProperty | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<PropertyEntry[]>([]);
  const { message } = AntApp.useApp();
  const bulkDelete = useBulkDeleteProperties();

  const applications = useApplications();
  const properties = useProperties({
    application,
    name: nameFilter,
    target: targetFilter,
    value: valueFilter,
    page,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    if (!application && applications.data && applications.data.length > 0) {
      const first = applications.data[0];
      if (first) {
        setSearchParams({ app: first.application }, { replace: true });
      }
    }
  }, [application, applications.data, setSearchParams]);

  // The application can change through the selector OR browser history —
  // reset paging, filters and the open drawer in both cases.
  useEffect(() => {
    setPage(1);
    setNameFilter("");
    setValueFilter("");
    setTargetFilter("");
    setNameDraft("");
    setValueDraft("");
    setTargetDraft("");
    setOpenProperty(null);
  }, [application]);

  // Clear the bulk-selection whenever the visible rows change.
  useEffect(() => {
    setSelectedRows([]);
  }, [application, page, nameFilter, valueFilter, targetFilter]);

  // Deleting the last rows of the final page must not strand the user on an
  // empty page.
  const total = properties.data?.total;
  useEffect(() => {
    if (total !== undefined) {
      const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
      if (page > maxPage) {
        setPage(maxPage);
      }
    }
  }, [total, page]);

  const selectApplication = (app: string) => {
    setSearchParams({ app });
  };

  const rows = (properties.data?.properties ?? [])
    .map((property) => property.entry)
    .filter((entry): entry is PropertyEntry => entry !== null);

  const noApplications = applications.isSuccess && applications.data.length === 0;

  return (
    <div>
      <div className="page-toolbar">
        <Select
          style={{ width: 260 }}
          placeholder="application"
          showSearch
          value={application || undefined}
          onChange={selectApplication}
          loading={applications.isLoading}
          options={(applications.data ?? []).map((info) => ({
            value: info.application,
            label: (
              <span>
                {info.application} <span className="dim">({info.propertyCount})</span>
              </span>
            ),
          }))}
          filterOption={(input, option) =>
            (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
        <Input.Search
          style={{ width: 220 }}
          placeholder="filter by name"
          allowClear
          value={nameDraft}
          onChange={(event) => setNameDraft(event.target.value)}
          onSearch={(value) => {
            setNameFilter(value);
            setPage(1);
          }}
        />
        <Input.Search
          style={{ width: 220 }}
          placeholder="search in values"
          allowClear
          value={valueDraft}
          onChange={(event) => setValueDraft(event.target.value)}
          onSearch={(value) => {
            setValueFilter(value);
            setPage(1);
          }}
        />
        <Input.Search
          style={{ width: 220 }}
          placeholder="filter by target"
          allowClear
          value={targetDraft}
          onChange={(event) => setTargetDraft(event.target.value)}
          onSearch={(value) => {
            setTargetFilter(value);
            setPage(1);
          }}
        />
        <div style={{ flex: 1 }} />
        {selectedRows.length > 0 && (
          <Popconfirm
            title={`Delete ${selectedRows.length} selected ${selectedRows.length === 1 ? "entry" : "entries"}?`}
            description="Force-deletes each selected property at its target."
            okText="delete"
            okButtonProps={{ danger: true }}
            onConfirm={() =>
              bulkDelete.mutate(
                {
                  application,
                  entries: selectedRows.map((row) => ({ name: row.name, target: row.target })),
                },
                {
                  onSuccess: (res) => {
                    setSelectedRows([]);
                    void message.success(
                      `deleted ${res.deleted}${res.failed.length ? `, ${res.failed.length} failed` : ""}`,
                    );
                  },
                  onError: (error) => void message.error(error.message),
                },
              )
            }
          >
            <Button danger icon={<DeleteOutlined />} loading={bulkDelete.isPending}>
              Delete {selectedRows.length}
            </Button>
          </Popconfirm>
        )}
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          New property
        </Button>
        <Tooltip title="download all properties of this application as JSON">
          <Button
            icon={<DownloadOutlined />}
            disabled={!application}
            onClick={() => {
              const link = document.createElement("a");
              link.href = `/api/export?application=${encodeURIComponent(application)}`;
              link.click();
            }}
          >
            Export
          </Button>
        </Tooltip>
        <Button icon={<UploadOutlined />} disabled={!application} onClick={() => setImportOpen(true)}>
          Import
        </Button>
      </div>

      {noApplications ? (
        <Empty
          description="no applications yet — create the first property to get started"
          style={{ marginTop: 80 }}
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New property
          </Button>
        </Empty>
      ) : (
        <Table
          size="middle"
          rowKey={(entry) => JSON.stringify([entry.name, entry.target])}
          loading={properties.isLoading}
          dataSource={rows}
          rowSelection={{
            selectedRowKeys: selectedRows.map((row) => JSON.stringify([row.name, row.target])),
            onChange: (_keys, selected) => setSelectedRows(selected),
          }}
          onRow={(entry) => ({
            onClick: () => setOpenProperty({ name: entry.name, initialTarget: entry.target }),
          })}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: properties.data?.total ?? 0,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (total) => `${total} entries`,
          }}
          columns={[
            {
              title: "name",
              dataIndex: "name",
              render: (name: string) => <span className="mono" style={{ fontWeight: 500 }}>{name}</span>,
            },
            {
              title: "target",
              dataIndex: "target",
              render: (target: string) => <span className="target-chip">{target}</span>,
            },
            {
              title: "value",
              dataIndex: "value",
              render: (value: string, entry) => {
                const property = properties.data?.properties.find(
                  (candidate) =>
                    candidate.entry?.name === entry.name && candidate.entry?.target === entry.target,
                );
                if (value === "") {
                  return <span className="dim">(empty)</span>;
                }
                return (
                  <span className="value-preview">
                    {value}
                    {property?.valueTruncated ? "…" : ""}
                  </span>
                );
              },
            },
            {
              title: "description",
              dataIndex: "description",
              render: (description: string | null) =>
                description ? (
                  <Tooltip title={description}>
                    <span className="dim">
                      {description.length > 90 ? `${description.slice(0, 90)}…` : description}
                    </span>
                  </Tooltip>
                ) : (
                  <span className="dim">—</span>
                ),
            },
          ]}
        />
      )}

      <PropertyDrawer
        application={application}
        property={openProperty}
        onClose={() => setOpenProperty(null)}
      />
      <CreatePropertyModal
        application={application}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(name, target) => {
          setCreateOpen(false);
          setOpenProperty({ name, initialTarget: target });
        }}
      />
      <ImportModal application={application} open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}
